// Address search for placing a site, on OpenStreetMap's Nominatim, within
// its usage policy (operations.osmfoundation.org/policies/nominatim):
// - a search only when the user asks for it (Enter or the button), never
//   as they type: the policy forbids autocomplete on its servers;
// - at most one request per second from this page, and the same search
//   is answered from memory;
// - the browser sends the app's origin as Referer (a page cannot set its
//   own User-Agent); results limited to Suriname;
// - the screen shows "© OpenStreetMap contributors".
// If it fails, the caller says so and the point can still be placed by
// hand, by link or by the phone's location.

const ENDPOINT = 'https://nominatim.openstreetmap.org/search';
const MIN_GAP_MS = 1100;
const round6 = (n) => Math.round(n * 1e6) / 1e6;

let lastRequest = 0;
const cache = new Map();

/** For tests: forget the cache and the last request time */
export const resetAddressSearch = () => {
  lastRequest = 0;
  cache.clear();
};

/**
 * Up to 5 places in Suriname for `query`: [{ label, lat, lng }]. Throws
 * when Nominatim does not answer properly.
 */
export const searchAddress = async (query, {
  language = 'nl',
  fetchImpl = (...args) => fetch(...args),
  now = () => Date.now(),
  sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); }),
} = {}) => {
  const q = String(query || '').trim().replace(/\s+/g, ' ');
  if (q.length < 3) return [];
  const key = `${language}|${q.toLowerCase()}`;
  if (cache.has(key)) return cache.get(key);
  const wait = lastRequest + MIN_GAP_MS - now();
  if (wait > 0) await sleep(wait);
  lastRequest = now();
  const params = new URLSearchParams({
    format: 'jsonv2', q, countrycodes: 'sr', limit: '5', 'accept-language': language === 'nl' ? 'nl,en' : 'en,nl',
  });
  const res = await fetchImpl(`${ENDPOINT}?${params}`, {
    headers: { Accept: 'application/json' },
    referrerPolicy: 'strict-origin-when-cross-origin',
  });
  if (!res.ok) throw new Error(`Address search answered ${res.status}`);
  const rows = await res.json();
  const results = (Array.isArray(rows) ? rows : [])
    .map((r) => ({ label: String(r.display_name || ''), lat: round6(Number(r.lat)), lng: round6(Number(r.lon)) }))
    .filter((r) => r.label && Number.isFinite(r.lat) && Number.isFinite(r.lng));
  cache.set(key, results);
  return results;
};
